import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import billingService from '../services/billingService';
import { BillingClaimDTO, ClaimStatus } from '../types/billing.types';
import { formatToUIDate, formatToUIDateTime } from '../utils/dateUtils';

const BillingPage: React.FC = () => {
  const [claims, setClaims] = useState<BillingClaimDTO[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<BillingClaimDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [claimTypeFilter, setClaimTypeFilter] = useState('All');
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);

  // Fetch claims on component mount
  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const response = await billingService.getAllBillingClaims(0, 100);
      const claimsList = response.content || [];
      setClaims(claimsList);
      setFilteredClaims(claimsList);
    } catch (error: any) {
      console.error('Error fetching billing claims:', error);
      setClaims([]);
      setFilteredClaims([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = claims;

    if (searchTerm) {
      result = result.filter(claim =>
        (claim.residentName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        claim.residentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (claim.payerName?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'All') {
      result = result.filter(claim => claim.status === statusFilter);
    }

    if (claimTypeFilter !== 'All') {
      result = result.filter(claim => claim.claimType === claimTypeFilter);
    }

    setFilteredClaims(result);
    setPage(1); // Reset to first page when filtering
  }, [searchTerm, statusFilter, claimTypeFilter]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const paginatedClaims = filteredClaims.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Billing & Reimbursement
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Billing Stats */}
      {!loading && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#1976d220', mr: 2 }}>
                <ReceiptIcon sx={{ color: '#1976d2', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4">{claims.length}</Typography>
                <Typography variant="body2" color="text.secondary">Total Claims</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#388e3c20', mr: 2 }}>
                <MoneyIcon sx={{ color: '#388e3c', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4">{claims.filter(c => c.status === 'PAID').length}</Typography>
                <Typography variant="body2" color="text.secondary">Paid Claims</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#f57c0020', mr: 2 }}>
                <TrendingUpIcon sx={{ color: '#f57c00', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4">{claims.filter(c => c.status === 'IN_REVIEW' || c.status === 'SUBMITTED').length}</Typography>
                <Typography variant="body2" color="text.secondary">Pending Claims</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: '#7b1fa220', mr: 2 }}>
                <MoneyIcon sx={{ color: '#7b1fa2', fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="h4">${claims.reduce((sum, c) => sum + (c.billedAmount || 0), 0).toFixed(2)}</Typography>
                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tabs for different billing views */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Claims Management" />
          <Tab label="Revenue Reports" />
          <Tab label="Payer Management" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Paper sx={{ p: 2 }}>
          {/* Filters and Search */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <TextField
              variant="outlined"
              placeholder="Search claims..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 250 }}
            />

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as string)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SUBMITTED">Submitted</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="IN_REVIEW">In Review</MenuItem>
                <MenuItem value="PARTIAL_PAID">Partial Paid</MenuItem>
                <MenuItem value="DENIED">Denied</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Claim Type</InputLabel>
              <Select
                value={claimTypeFilter}
                label="Claim Type"
                onChange={(e) => setClaimTypeFilter(e.target.value as string)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="SNF">SNF</MenuItem>
                <MenuItem value="Hospice">Hospice</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                // Reset filters
                setSearchTerm('');
                setStatusFilter('All');
                setClaimTypeFilter('All');
              }}
            >
              Reset
            </Button>
          </Box>

          {/* Claims Table */}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Claim Number</TableCell>
                  <TableCell>Resident ID</TableCell>
                  <TableCell>Claim Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Service Date</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClaims.length > 0 ? (
                  paginatedClaims.map((claim) => (
                    <TableRow key={claim.id}>
                      <TableCell>
                        <Typography variant="body2">{claim.claimNumber || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>{claim.residentId}</TableCell>
                      <TableCell>
                        <Chip
                          label={claim.claimType}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>${(claim.billedAmount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={claim.status}
                          size="small"
                          color={
                            claim.status === 'PAID' ? 'success' :
                              claim.status === 'IN_REVIEW' || claim.status === 'SUBMITTED' ? 'warning' :
                                claim.status === 'DENIED' ? 'error' : 'info'
                          }
                        />
                      </TableCell>
                      <TableCell>{claim.serviceDateFrom ? formatToUIDate(claim.serviceDateFrom) : 'N/A'}</TableCell>
                      <TableCell>{claim.createdAt ? formatToUIDateTime(claim.createdAt) : 'N/A'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No claims found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredClaims.length / rowsPerPage)}
              page={page}
              onChange={(event, value) => setPage(value)}
            />
          </Box>
        </Paper>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Revenue Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Revenue reports and analytics coming soon. This section will provide detailed insights into billing performance, payer mix, and reimbursement trends.
            </Typography>
            <Box sx={{ mt: 2, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc' }}>
              <Typography variant="h6" color="text.secondary">
                Revenue Chart Visualization
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payer Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Payer management tools coming soon. This section will allow you to manage insurance details, contracts, and payer-specific requirements.
            </Typography>
            <Box sx={{ mt: 2, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc' }}>
              <Typography variant="h6" color="text.secondary">
                Payer Management Tools
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BillingPage;